import React, { useState, useEffect } from 'react';
import { ASSETS } from '../assetManifest';

const BASE_Z_INDEX = -2000;

const Sprite = React.forwardRef(({
  emoji,
  size = 40,
  style = {},
  className = '',
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={className}
      style={{
        fontSize: `${size}px`,
        lineHeight: 1,
        height: `${size}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
      {...props}
    >
      {emoji}
    </div>
  );
});

Sprite.displayName = 'Sprite';

const Shadow = ({ width = 20, height = 6 }) => (
  <div
    style={{
      position: 'absolute',
      left: '50%',
      top: '100%',
      width: `${width}px`,
      height: `${height}px`,
      backgroundColor: 'rgba(0,0,0,0.3)',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)'
    }}
  />
);

// New component for displaying cooldown times
const CooldownDisplay = ({ passCooldown, fakeCooldown }) => {
  const showPass = passCooldown > 0;
  const showFake = fakeCooldown > 0;
  
  if (!showPass && !showFake) return null;
  
  return (
    <div className="flex flex-col items-center mt-1" style={{ fontSize: '10px', color: 'white' }}>
      {showPass && (
        <div className="flex items-center gap-1 mb-1">
          <span>Pass:</span>
          <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500"
              style={{ width: `${(passCooldown / 750) * 100}%` }}
            />
          </div>
          <span>{(passCooldown / 1000).toFixed(1)}s</span>
        </div>
      )}
      {showFake && (
        <div className="flex items-center gap-1">
          <span>"Pass":</span>
          <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500"
              style={{ width: `${(fakeCooldown / 250) * 100}%` }}
            />
          </div>
          <span>{(fakeCooldown / 1000).toFixed(1)}s</span>
        </div>
      )}
    </div>
  );
};

// Component for the string connection during fake passes
const FakePassString = ({ fromX, fromY, toX, toY }) => {
  // Calculate the angle for the line
  const angle = Math.atan2(toY - fromY, toX - fromX) * (180 / Math.PI);
  // Calculate the distance between the two points
  const distance = Math.sqrt(Math.pow(toX - fromX, 2) + Math.pow(toY - fromY, 2));
  
  return (
    <div
      style={{
        position: 'absolute',
        left: fromX,
        top: fromY,
        width: distance,
        height: 2,
        borderTop: '2px dashed rgba(255, 255, 255, 0.6)',
        transformOrigin: '0 0',
        transform: `rotate(${angle}deg)`,
        zIndex: BASE_Z_INDEX + 5,
      }}
    />
  );
};

function GameStage({ gameState, gameInterface }) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 10);
    return () => clearInterval(interval);
  }, []);

  const renderBomb = () => {
    const bomb = gameState.bomb;
    if (!bomb) return null;
    
    if (bomb.isExploding) {
      const holder = gameState.entities[bomb.holderId];
      if (!holder) return null;
      
      return (
        <div
          style={{
            position: 'absolute',
            left: holder.x,
            top: holder.y,
            transform: 'translate(-50%, -50%)',
            zIndex: BASE_Z_INDEX + 10,
          }}
        >
          <Sprite
            emoji="💥"
            size={bomb.size * 1.5}
            className="animate-pulse"
          />
        </div>
      );
    }
    
    if (bomb.isAnimating) {
      // Calculate position during animation
      const progress = bomb.animationProgress;
      const fromX = bomb.fromPosition?.x;
      const fromY = bomb.fromPosition?.y;
      const toX = bomb.toPosition?.x;
      const toY = bomb.toPosition?.y;
      
      if (fromX === undefined || fromY === undefined || toX === undefined || toY === undefined) {
        return null;
      }
      
      // Use bezier curve for more natural arc
      const controlX = (fromX + toX) / 2;
      const controlY = Math.min(fromY, toY) - 100;
      
      const t = progress;
      const u = 1 - t;
      const x = u * u * fromX + 2 * u * t * controlX + t * t * toX;
      const y = u * u * fromY + 2 * u * t * controlY + t * t * toY;
      
      // Calculate danger level (0-1)
      const dangerLevel = (bomb.size - bomb.bombStartSize) / (bomb.bombExplodeSize - bomb.bombStartSize);
      const bombColor = dangerLevel > 0.7 ? 'red' : (dangerLevel > 0.4 ? 'orange' : 'black');
      
      // Render the string connection for fake passes
      const stringConnection = bomb.isFakePass ? (
        <FakePassString fromX={fromX} fromY={fromY} toX={x} toY={y} />
      ) : null;
      
      return (
        <>
          {stringConnection}
          <div
            style={{
              position: 'absolute',
              left: x,
              top: y,
              transform: 'translate(-50%, -50%)',
              zIndex: BASE_Z_INDEX + 10,
            }}
          >
            <div className="flex flex-col items-center">
              <Sprite
                emoji="💣"
                size={bomb.size}
                style={{ filter: `drop-shadow(0 0 ${dangerLevel * 10}px ${bombColor})` }}
                className={dangerLevel > 0.7 ? 'animate-pulse' : ''}
              />
              <Shadow width={bomb.size * 0.6} height={bomb.size * 0.15} />
            </div>
          </div>
        </>
      );
    }
    
    // Render bomb at holder position
    const holder = gameState.entities[bomb.holderId];
    if (!holder) return null;
    
    // Calculate danger level (0-1)
    const dangerLevel = (bomb.size - bomb.bombStartSize) / (bomb.bombExplodeSize - bomb.bombStartSize);
    const bombColor = dangerLevel > 0.7 ? 'red' : (dangerLevel > 0.4 ? 'orange' : 'black');
    
    return (
      <div
        style={{
          position: 'absolute',
          left: holder.x,
          top: holder.y,
          transform: 'translate(-50%, -50%)',
          zIndex: BASE_Z_INDEX + 10,
        }}
      >
        <div className="flex flex-col items-center">
          <Sprite
            emoji="💣"
            size={bomb.size}
            style={{ filter: `drop-shadow(0 0 ${dangerLevel * 10}px ${bombColor})` }}
            className={dangerLevel > 0.7 ? 'animate-pulse' : ''}
          />
          <Shadow width={bomb.size * 0.6} height={bomb.size * 0.15} />
        </div>
      </div>
    );
  };

  return (
    <div className="h-full w-full relative" style={{ transform: 'translateZ(0)' }}>
      <div
        className="h-full w-full relative overflow-hidden"
        style={{ 
          zIndex: BASE_Z_INDEX, 
          userSelect: 'none',
          backgroundImage: `url(${ASSETS.backgrounds.gameBackground.path})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      {/* Center circle */}
      <div
        style={{
          position: 'absolute',
          left: gameState.centerPosition.x,
          top: gameState.centerPosition.y,
          width: '100px',
          height: '100px',
          border: '2px dashed rgba(255,255,255,0.3)',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: BASE_Z_INDEX + 1,
        }}
      />
      
      {/* Players */}
      {Object.values(gameState.entities).map((entity) => (
        <div
          key={entity.id}
          style={{
            position: 'absolute',
            left: entity.x,
            top: entity.y,
            transform: 'translate(-50%, -50%)',
            zIndex: Math.floor(entity.y) + BASE_Z_INDEX,
            opacity: entity.isEliminated ? 0.5 : 1,
          }}
        >
          <div className="flex flex-col items-center gap-1">
            <div style={{ color: 'white', marginBottom: '4px', textAlign: 'center' }}>
              {entity.name}
              {entity.isEliminated && ' (Eliminated)'}
            </div>
            <div>
              <Sprite
                emoji={entity.isEliminated ? '😵' : (entity.isLaughing ? '😂' : (entity.hasBomb ? '😰' : '😐'))}
                size={40}
                className={entity.isLaughing ? 'animate-bounce' : ''}
              />
            </div>
            <Shadow />
            
            {/* Display cooldown times */}
            {!entity.isEliminated && (
              <CooldownDisplay 
                passCooldown={entity.passCooldown} 
                fakeCooldown={entity.fakeCooldown} 
              />
            )}
          </div>
        </div>
      ))}
      
      {/* Render bomb */}
      {renderBomb()}
    </div>
  );
}

export default GameStage;
